import { MathUtils } from '@printu/geom';
import { hitTestMarker } from 'app/core/markers';
import { getMarkerLayer as getMarkerLayerCore, MarkerTypeInheritance, } from 'app/core/markers/models';
import { getPageBleedBox, getPageRect } from 'app/stores/project/page';
import { getMarkersAlias, getMarkersProperty } from 'app/utils/varia';
import { getProjectState } from './project';
// -----------------------
// Pages
// -----------------------
export const getPagesCount = (state) => getProjectState(state).pages.length;
export const hasPages = (state) => getProjectState(state).activePage && getPagesCount(state) > 0;
export const getCurrentPageId = (state) => getProjectState(state).activePage;
export const getCurrentPage = (state) => getProjectState(state).page[getCurrentPageId(state)];
export const getCurrentPageIndex = (state) => getProjectState(state).pages.indexOf(getCurrentPageId(state));
export const getPagesIds = (state) => getProjectState(state).pages;
export const getPages = (state) => getProjectState(state).pages.map(pageId => getPage(state, pageId));
export const getPage = (state, pageId) => pageId && getProjectState(state).page[pageId];
export const getPageAt = (state, index) => getProjectState(state).page[getProjectState(state).pages[index]];
export const getPageImage = (state, pageId) => {
    const page = getPage(state, pageId);
    return page && getProjectState(state).images[page.image];
};
export const getPageByIdOrActive = (state, id) => getPage(state, id !== null && id !== void 0 ? id : getCurrentPageId(state));
export const getImageMap = (state) => getProjectState(state).images;
export const getImage = (state, id) => getProjectState(state).images[id];
export function getCurrentSlideImageSize(state) {
    const page = getCurrentPage(state);
    if (!page) {
        return undefined;
    }
    const image = getPageImage(state, page.id);
    if (!image) {
        return undefined;
    }
    return {
        id: page.id,
        width: image.size.width,
        height: image.size.height,
        crop: page.crop,
    };
}
export function getCurrentSlideSize(state) {
    const page = getCurrentPage(state);
    if (!page) {
        return undefined;
    }
    const image = getPageImage(state, page.id);
    if (!image) {
        return undefined;
    }
    const { width, height } = getPageBleedBox(page, image);
    return {
        id: page.id,
        width,
        height,
    };
}
// -----------------------
// Markers
// -----------------------
export const getMarker = (state, id) => getProjectState(state).markers[id];
export const getHoveredMarkerId = (state) => getProjectState(state).hovered;
export const getSelection = (state) => getProjectState(state).selection;
export const getSelectedMarkers = (state) => getProjectState(state).selection.map(itemId => getProjectState(state).markers[itemId]);
export const getSelectedMarkersAlias = (state) => {
    var _a, _b;
    return (_b = (_a = getProjectState(state).selection.map(itemId => getProjectState(state).markers[itemId])) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.alias;
};
export const hasSelectedUnlockedMarkers = (state, expectedLength, alias) => {
    const selectedMarkers = getSelectedMarkers(state);
    return (selectedMarkers.length === expectedLength &&
        getMarkersAlias(selectedMarkers) === alias &&
        getMarkersProperty(selectedMarkers, 'locked') !== true);
};
export const getSelectionAlias = (state) => {
    const selection = getSelection(state);
    if (!selection || !selection.length) {
        return null;
    }
    const selectionInheritance = selection.reduce((inheritance, itemId) => {
        const item = getProjectState(state).markers[itemId];
        const itemInheritance = MarkerTypeInheritance[item.alias];
        if (!inheritance) {
            return [...itemInheritance];
        }
        return itemInheritance.reduce((commonInheritance, alias, index) => {
            if (alias === inheritance[index]) {
                commonInheritance.push(alias);
            }
            return commonInheritance;
        }, []);
    }, undefined);
    const size = selectionInheritance.length;
    return size > 0 ? selectionInheritance[size - 1] : null;
};
export const isSelectionTextBased = (state) => 
// should also consider note, pin?
getSelectionAlias(state) === 'Text';
export const getCurrentPageMarkers = (state) => {
    const page = getCurrentPage(state);
    const { blurs, marks, spots, pins, texts } = page !== null && page !== void 0 ? page : {};
    return {
        blurs,
        marks,
        spots,
        pins,
        texts,
    };
};
export const getCurrentPageMarkersCount = (state) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const markers = getCurrentPageMarkers(state);
    return markers
        ? ((_b = (_a = markers.marks) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) +
            ((_d = (_c = markers.spots) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) +
            ((_f = (_e = markers.pins) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0) +
            ((_h = (_g = markers.texts) === null || _g === void 0 ? void 0 : _g.length) !== null && _h !== void 0 ? _h : 0)
        : 0;
};
export const hasSelection = (state) => getProjectState(state).selection.length > 0;
export const hasSelectedLockedItems = (state) => {
    const { selection } = getProjectState(state);
    if (!selection.length) {
        return false;
    }
    // selection is locked when there is at least one locked item
    return selection.some(itemId => {
        const marker = getProjectState(state).markers[itemId];
        if (marker) {
            return marker.locked === true;
        }
        return false;
    });
};
export const isMarkerSelected = (state, id) => getProjectState(state).selection.includes(id);
export const getActiveTool = (state) => {
    const activeToolId = state.editor.activeTool;
    return state.editor.availableTools.find(tool => tool.id === activeToolId);
};
export const getMarkerLayer = (state, id) => {
    var _a;
    const alias = (_a = getProjectState(state).markers[id]) === null || _a === void 0 ? void 0 : _a.alias;
    return alias ? getMarkerLayerCore(alias) : 0;
};
export const getPinsCount = (state) => getCurrentPage(state).pins.length;
export const getMaximumPinNumber = (state) => {
    const page = getCurrentPage(state);
    return page.pins
        .map(id => getProjectState(state).markers[id])
        .map(pin => pin.number)
        .reduce((max, value) => (max > value ? max : value), 0);
};
// -----------------------
// Viewport size & style
// -----------------------
export const getSizeFactor = (state) => {
    var _a, _b, _c, _d;
    const smartScalingMarkers = configGet('smartScalingMarkers', true);
    if (!smartScalingMarkers) {
        return 1;
    }
    // we want paint size to be same for different images (~similar to pt)
    const fitWidth = (_b = (_a = window.screen) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : window.innerWidth;
    const fitHeight = (_d = (_c = window.screen) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : window.innerHeight;
    const page = getCurrentPage(state);
    const image = getPageImage(state, page.id);
    const pageRect = getPageRect(page, image);
    const { width, height } = pageRect !== null && pageRect !== void 0 ? pageRect : {
        width: fitWidth,
        height: fitHeight,
    };
    const scaleFactor = MathUtils.getFitScale(width, height, fitWidth, fitHeight);
    return Math.min(1, scaleFactor);
};
export const getInsertStyle = (state) => {
    var _a, _b;
    const { editor } = state;
    const project = getProjectState(state);
    const color = (_b = (_a = editor.markerColor) === null || _a === void 0 ? void 0 : _a[editor.activeTool]) !== null && _b !== void 0 ? _b : project.color;
    return {
        color,
        size: project.size,
        sizeFactor: getSizeFactor(state),
        viewportZoom: state.viewport.zoom,
    };
};
// -----------------------
// Select based on hit test
// -----------------------
export const getMarkerIdAtPoint = (state, point) => {
    let markerId;
    const { markers } = getProjectState(state);
    const pageMarkers = getCurrentPageMarkers(state);
    // texts
    markerId = pageMarkers.texts.find(id => hitTestMarker(markers[id], point));
    if (!markerId) {
        markerId = pageMarkers.pins.find(id => hitTestMarker(markers[id], point));
    }
    // marks
    if (!markerId) {
        markerId = pageMarkers.marks.find(id => hitTestMarker(markers[id], point));
    }
    // spots
    if (!markerId) {
        markerId = pageMarkers.spots.find(id => hitTestMarker(markers[id], point));
    }
    // blurs
    if (!markerId) {
        markerId = pageMarkers.blurs.find(id => hitTestMarker(markers[id], point));
    }
    return markerId;
};
